/**
 * When does the identity shell take the screen, and with which candidates?
 *
 * ONE implementation, called by BOTH shells. `MobileShell` replaces `AppLayout`
 * outright on a phone viewport (`isPhone ? <MobileShell/> : <AppLayout/>`), so
 * anything decided inline in one of them simply does not exist in the other —
 * which is exactly how the picker and the org-less screen came to be
 * desktop-only for a commit. `identity-only-spa.test.ts` already warns about
 * this shape in the other direction ("a second inline list here is how the two
 * shells come to disagree about what a mode means").
 *
 * Deliberately PURE and source-agnostic: the desktop shell fetches
 * `my-tenants` imperatively into `useState`, the phone reads the shared
 * `['mobile-my-tenants']` TanStack cache. Only the RULES are shared; how the
 * list and the settled flag are obtained is each shell's own business.
 *
 * It reads the mode from the TENANT LIST rather than taking the shell's derived
 * flag, and that is the point rather than an oversight: the flag ORs in a polled
 * source that lags a tenant switch, so a decision built from both could describe
 * two different moments at once. `isIdentityOnlyTenant` is the same predicate
 * both shells already read, so no fourth spelling of the rule appears (C5) —
 * only one more reader of it.
 */
import { matchRoutes } from 'react-router-dom'
import { isIdentityOnlyTenant } from '@papan/orgs/client'
import type { LandingExperience, TenantMode } from '@papan/types'

export interface ShellTenant {
  id: string
  name: string
  type: string
  mode?: TenantMode
  emails: Array<{ email: string; role: string }>
}

export interface IdentityShellInput {
  /** Every tenant from `my-tenants`. Empty while unresolved — which is why
   *  `settled` exists and why no count below may be read without it. */
  tenants: ShellTenant[]
  /** The active tenant id. */
  tenantId: string
  /**
   * The URL already names an organization (`/t/<id>/…`).
   *
   * Suppresses the picker, because the destination is not in question: a
   * shared link, an emailed link or a bookmark has ALREADY answered "which
   * organization?". Asking anyway both interrupts and destroys the answer —
   * `switchTenant` always ends in `navigate(landingPath(...))`, so picking the
   * very org the link named still dumped the person on that org's home surface
   * with the rest of the path gone. Verified: `/t/acme/folder` → pick Acme →
   * `/t/acme/launcher`.
   *
   * The sign-in case is untouched. The root redirect lands on `/~/launcher`,
   * which names no organization, so the picker still fires there.
   *
   * A `/~/…` deep link names the personal drive the same way (#1253) — but that
   * half is decided HERE from `entryPath`, not by the caller, because it has
   * exclusions only this module can see (the landing view, an identity-only
   * personal tenant). Pass only the `/t/<id>/…` fact.
   */
  tenantInUrl: boolean
  /**
   * Where this page load ENTERED the app (`useTenant().entryPath`; see
   * `nextEntry`), `''` when it was no destination. The live path only until the
   * entry is captured (it is then the same thing); never afterwards.
   *
   * Live afterwards would be wrong: someone who opened `/~/folder/x` (no picker, nothing
   * chosen) and later pressed Home lands on `/~/launcher`, and a live check
   * would throw the picker up mid-session. A `/t/<id>/…` entry counts for the
   * same reason: a notification can take that person into `/~/…`.
   */
  entryPath: string
  /** The deployment's landing view (`useLandingExperience()`): `/~/<this>` is
   *  where sign-in lands everyone, so it is not a choice (#1253). */
  landingView: LandingExperience
  /**
   * Has `my-tenants` answered, either way?
   *
   * ⚠ THE gate. `tenants.length === 0` is also what an unanswered request looks
   * like, and an unresolved list makes the active tenant look full — so every
   * boolean below is false while unsettled, no matter what the counts say.
   * Without this the org-less takeover paints over every user's screen on every
   * cold load: invisible on a dev machine, plain on a throttled connection.
   */
  settled: boolean
  /** The person has already chosen a destination this page load. */
  chosen: boolean
}

export interface IdentityShellDecision {
  /** The picker's rows: every tenant worth working in, INCLUDING the active
   *  one. A full person's own drive is a real destination and wears the active
   *  card; an identity-only tenant is excluded by the same predicate, which is
   *  C1's "no dead-end Personal entry" arriving without a special case. */
  switchable: ShellTenant[]
  /** Auto-switch candidates — never the tenant we are already in. */
  fullTenants: ShellTenant[]
  /**
   * This person's OWN personal tenant is identity-only — so it is never a
   * destination, WHICHEVER tenant they are currently standing in.
   *
   * ⚠ Not the same question as `identityOnly`, and conflating them shipped a
   * bug. `identityOnly` describes the ACTIVE tenant, so once an identity-only
   * person switches into one of their organizations it goes false — and the
   * avatar menu happily offered "Personal" again, a one-click route to a drive
   * that does not exist. Worse, landing there fires neither shell state (the
   * picker is spent, and the org-less screen wants zero full tenants), so the
   * person is deposited on a stripped surface with no explanation.
   *
   * C1 reads as "no personal entry in the avatar menu in IOM", and THIS is what
   * that means: the entry is dead for as long as the tenant behind it is.
   *
   * Fails OPEN (an unresolved list yields `false`, i.e. the entry shows), which
   * matches `isIdentityOnlyTenant`'s own default and keeps this disclosure
   * rather than enforcement.
   */
  personalIsIdentityOnly: boolean
  /** What the picker's threshold counts. See `pickerActive`. */
  fullOrgs: ShellTenant[]
  /**
   * Show the picker.
   *
   * TWO OR MORE ORGANIZATIONS, whatever mode the tenant is in (decision
   * 2026-08-14, closing the seam §8.2 left open). Counting TENANTS instead
   * would fire for anyone with one org plus their own drive — two destinations
   * and no real choice — i.e. most of the user base, for nothing.
   */
  pickerActive: boolean
  /**
   * Show the org-less state. Identity-only ONLY: a full person with no
   * organization has their own drive and is missing nothing, so "no
   * organization yet" would be a lie.
   */
  orgLessActive: boolean
  /** Either state is up. */
  active: boolean
  /**
   * The single tenant to move to without asking, or `null`.
   *
   * EXACTLY one, and identity-only only. Taking the first of several is a
   * silent choice between destinations only the person can rank, and it is not
   * even stable — it follows `my-tenants` order. The caller still owns the
   * `orgEmailsHydrated` wait and the once-per-tenant guard; this only answers
   * "is there an unambiguous destination".
   */
  autoSwitchTarget: ShellTenant | null
}

/**
 * Is this person's OWN personal tenant identity-only?
 *
 * Exported separately because the phone launcher's switcher needs exactly this
 * one answer and has no use for the rest of the decision. It is the SAME
 * predicate `decideIdentityShell` returns, so the two switchers cannot disagree
 * about whether "Personal" is a real destination.
 *
 * ⚠ Not "is the ACTIVE tenant identity-only". That is a different question, and
 * answering this one with it put a dead-end "Personal" row back in the menu the
 * moment such a person entered an organization.
 */
export function personalTenantIsIdentityOnly(tenants: ShellTenant[]): boolean {
  return tenants.some(t => t.type === 'personal' && isIdentityOnlyTenant(t))
}

/**
 * Does `path` name a page in the personal drive (#1253, owner decision
 * 2026-10-23)? A `/~/…` link answers "which drive?" exactly as `/t/<org>/…`
 * does — a shared link, a bookmark, a home-screen icon (a saved `/~/folder/<id>`).
 *
 * NOT the landing: `/`, `/~` and `/~/<landing view>` are where the sign-in
 * redirect puts everyone, so arriving there is no choice the person made, and
 * people with 2+ organizations must still be asked there (decision 2026-08-14).
 * Excluding only `/` would silently drop the sign-in picker, because the
 * redirect lands on `/~/launcher`, not `/`. Both questions — "is it under `/~`",
 * "is it the landing" — are asked of the ROUTER (`matchRoutes`: its decoding,
 * case-insensitivity and trailing slash, as it serves the page; the phone
 * renders `/~/Launcher` as the landing), and only the landing page itself: a page
 * under it (`/~/chat/<id>`) is a real page.
 *
 * Whether a route SERVES the path is not this function's question — a shell
 * tells us through `EntryUnroutedMark` (a stale `/~/miniapps` bookmark, a typo).
 */
export function isPersonalDeepLink(path: string, landingView: LandingExperience): boolean {
  // One match, the router's own (its decoding, case-insensitivity, trailing
  // slash, ranking): the static /~ and /~/<landing> outrank the splat.
  return matchRoutes([{ path: '/~' }, { path: `/~/${landingView}` }, { path: '/~/*' }], path)?.[0]?.route.path === '/~/*'
}

/** A `/t/<org>/…` path, as the router matches it. */
export function isOrgDeepLink(path: string): boolean {
  return !!matchRoutes([{ path: '/t/:tenant/*' }], path)
}

/** Which shell captured the entry — the two route tables differ. */
export type ShellKind = 'desktop' | 'phone'

/**
 * Where this page load ENTERED the app — the `entryPath` input (#1253). Held by
 * `TenantProvider`, beside `tenantChosen` (the other "a choice was made" fact),
 * so it outlives a shell swap at the phone breakpoint and shares its reset.
 *
 * `undefined` = not captured yet (the shell then reads the LIVE path — exactly
 * what will be captured, since no route has rendered); `path: ''` = no
 * destination (no route served it, or another account took over in place).
 */
export type EntryState = { path: string; shell?: ShellKind } | undefined

export type EntryEvent =
  /** The routed tree committed at `path` (`EntryCapture`). First one wins. */
  | { type: 'captured'; path: string; shell: ShellKind }
  /** `shell`'s catch-all caught `path` (`EntryUnroutedMark`). Voids the entry
   *  only if that is the entry itself and that shell captured it: the phone has
   *  no `/~/bin`, `/~/shared`, … route, and crossing the breakpoint must not void
   *  an entry the desktop served; a catch-all later in the session (the entry
   *  long since left) leaves it alone. */
  | { type: 'unrouted'; path: string; shell: ShellKind }
  /** The RENDERED account changed (`TenantProvider`, at commit). */
  | { type: 'account'; from: string | null; to: string | null }

/**
 * The one rule for the entry. Account changes, judged on what the UI RENDERED:
 * signed out (→ null) forgets it, so the next sign-in in this load captures
 * afresh; another account in place (A → B, with no navigation) VOIDS it — the
 * page on screen is A's, not a choice B made; the first sign-in (null → A)
 * changes nothing. A → null → A within one render is no change at all.
 */
export function nextEntry(state: EntryState, ev: EntryEvent): EntryState {
  switch (ev.type) {
    case 'captured':
      return state ?? { path: ev.path, shell: ev.shell }
    case 'unrouted':
      return state?.path && state.shell === ev.shell && state.path === ev.path ? { path: '' } : state
    case 'account':
      if (ev.from === ev.to || ev.from === null) return state
      return ev.to === null ? undefined : { path: '' }
  }
}

export function decideIdentityShell(input: IdentityShellInput): IdentityShellDecision {
  const { tenants, tenantId, settled, chosen } = input
  const personalIsIdentityOnly = personalTenantIsIdentityOnly(tenants)
  /**
   * The URL already names the destination: `/t/<id>/…` live (the caller's
   * fact), or the page this load ENTERED at — a `/t/<id>/…` or a `/~/…` deep
   * link (#1253) that a route actually served. The entry, not the live path, so
   * pressing Home or following a notification into `/~/…` later does not throw
   * the picker up mid-session.
   *
   * The entry half is void for an identity-only personal tenant: its `/~/` is a
   * drive that does not exist for them, so it cannot be a choice — they keep the
   * picker, or the one-org auto-switch, exactly as before. Without this, someone
   * with 2+ orgs could be left on the stripped personal surface with no picker
   * (the auto-switch only fires with exactly one org). That covers a `/t/` entry
   * too: they can still be taken into `/~/` from there.
   *
   * Deliberately NOT routed through `chosen`: `!chosen` also gates the
   * auto-switch, and is load-bearing there (see `autoSwitchTarget`).
   */
  const entryNamesDestination = isOrgDeepLink(input.entryPath)
    || isPersonalDeepLink(input.entryPath, input.landingView)
  const tenantInUrl = input.tenantInUrl || (entryNamesDestination && !personalIsIdentityOnly)

  /**
   * The ACTIVE tenant's mode, read from the SAME list every count below comes
   * from — deliberately NOT the shell's OR'd `identityOnly`.
   *
   * That flag ORs in a POLLED `write-status`, and `WriteStatusProvider` does not
   * reset on tenant change: it keeps the PREVIOUS tenant's answer until the new
   * fetch returns. So immediately after an auto-switch into an org, the flag
   * still said "identity-only" while `fullTenants` had already become empty (the
   * org is now the active tenant, so it is excluded) — and `orgLessActive` fired,
   * painting "No organization yet", plus its `inert` sweep, over the org the
   * person had just landed in.
   *
   * Desktop got away with it as a race (`tenantsSettled` flips false on tenant
   * change, so it needed `my-tenants` to win). On the phone it was not a race at
   * all: `settled` is TanStack's `isFetched`, which never returns to false.
   *
   * Deriving from the list makes the decision internally consistent — one
   * snapshot answers both "what mode am I in" and "what else is there". The cost
   * is that an operator stripping a drive mid-session no longer flips this until
   * `my-tenants` is re-read; the caller's reconcile effect closes that, and the
   * drive's own notice covers the interim.
   */
  const activeIsIdentityOnly = tenants.some(t => t.id === tenantId && isIdentityOnlyTenant(t))

  const switchable = tenants.filter(t => !isIdentityOnlyTenant(t))
  const fullTenants = switchable.filter(t => t.id !== tenantId)
  const fullOrgs = switchable.filter(t => t.type === 'organization')

  const pickerActive = settled && !chosen && !tenantInUrl && fullOrgs.length >= 2
  const orgLessActive = settled && activeIsIdentityOnly && fullTenants.length === 0

  return {
    switchable,
    fullTenants,
    // Asked of the PERSONAL tenant in the list, not of the active one.
    personalIsIdentityOnly,
    fullOrgs,
    pickerActive,
    orgLessActive,
    active: pickerActive || orgLessActive,
    /**
     * ⚠ `!chosen` is LOAD-BEARING, and its absence was a shipped bug: picking
     * one of two organizations landed you in the OTHER one.
     *
     * Choosing org A makes A active, so `fullTenants` — everything full except
     * the active tenant — becomes exactly [B]. That is the auto-switch's
     * trigger. `identityOnly` does not save you: it ORs in a POLLED
     * write-status that still reports the personal tenant's `true` for a beat
     * after the switch, and `autoSwitchedRef` does not either, because it is
     * keyed on the tenant id and has never seen the newly-active one.
     *
     * So the guard has to be the CHOICE itself: once a person has named a
     * destination, nothing may relocate them for the rest of the page load.
     * That also gives the right answer for someone who picks and then switches
     * back to their identity-only tenant by hand — they stay there, which is
     * the same intent `autoSwitchedRef` was protecting.
     *
     * `settled` is gated for the usual reason: an empty list mid-flight must
     * read as "not known yet", not "no candidates".
     */
    autoSwitchTarget:
      !chosen && settled && activeIsIdentityOnly && fullTenants.length === 1 ? fullTenants[0]! : null,
  }
}
